import React, { useEffect, useRef, useState } from "react";
import {
  DeleteOutlined,
  BoxIcons,
  IconTypes,
  PushpinOutlined,
  PushpinFilled,
  EditOutlined,
  CheckOutlined,
  CloseOutlined,
} from "us.icons";
import Common from "us.common";
import { useTranslation } from "react-i18next";
import { IMessageTemplate } from "../../Interface";
import { isSubCaseTransferredToCase } from "us.common/functions";
import { useFormikContext } from "formik";
import { debounce } from "lodash";

const { $Button, $Tooltip, $Popconfirm, $Input, $Tag } = Common.Components;

const MessageTemplate: React.FC<IMessageTemplate> = (props) => {
  const {
    messageTemplate,
    onDelete,
    onPreviewEditDrawer,
    metaData,
    onPinUnpinTemplate,
    save,
  } = props;

  const { t } = useTranslation();

  const [isDisplayNameEdit, setIsDisplayNameEdit] = useState<boolean>(false);
  const { values, isValid, setFieldValue } = useFormikContext<any>();

  const { plugin, isPin, displayName, id, templateId } = messageTemplate ?? {};

  const handleClick = () => {
    onPreviewEditDrawer && onPreviewEditDrawer(true, messageTemplate);
  };

  // Using useRef to store the debounced function
  const debouncedHandleClick = useRef(debounce(handleClick, 300)).current;

  // Cleanup the debounced function when the component unmounts
  useEffect(() => {
    return () => {
      debouncedHandleClick.cancel();
    };
  }, [debouncedHandleClick]);

  /**
   * Open Input field and set default display name
   * @param name - Current display name
   * @param setFieldValue - Formik prop
   */
  const editDisplayName = () => {
    setFieldValue("displayName", displayName, true);
    setIsDisplayNameEdit(true);
  };
  /**
   * Save new display name
   * @param newDisplayName - New name
   * @param setFieldValue - Formik prop
   */
  const saveDisplayName = () => {
    const { displayName: newDisplayName } = values;

    if (newDisplayName.length && displayName != newDisplayName) {
      save({ id, newDisplayName: newDisplayName });
    }
    setIsDisplayNameEdit(false);
    setFieldValue("displayName", "", true);
  };

  return (
    <div
      className={`msg-template-item ${
        isDisplayNameEdit ? "msg-template-item-edit" : ""
      }`}
      onClick={debouncedHandleClick}
    >
      <div className={`msg-template-item-content`}>
        <div className="msg-template-column">
          <div className="msg-template-icon">
            <BoxIcons type={IconTypes.BOX_ICON} name={plugin.toLowerCase()} />
          </div>

          {!isDisplayNameEdit && (
            <div className="msg-template-name" title={displayName}>
              <div className="d-flex align-items-center">
                <span>{displayName}</span>
              </div>
            </div>
          )}
          {isDisplayNameEdit && (
            <div
              className="msg-template-name"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <$Input
                    required
                    name="displayName"
                    size="small"
                    className="w-100"
                  />
                </div>
                <div className="d-flex align-items-center">
                  <$Tooltip
                    placement="top"
                    title={t("US.COLLECTION.COMMON:COMMON.SAVE")}
                  >
                    <$Button
                      size="small"
                      type="primary"
                      onClick={() => saveDisplayName()}
                      className="ml-1"
                      disabled={!isValid}
                    >
                      <CheckOutlined />
                    </$Button>
                  </$Tooltip>
                  <$Tooltip
                    placement="top"
                    title={t("US.COLLECTION.COMMON:COMMON.CANCEL")}
                  >
                    <$Button
                      size="small"
                      onClick={() => setIsDisplayNameEdit(false)}
                      className="ml-1"
                    >
                      <CloseOutlined />
                    </$Button>
                  </$Tooltip>
                </div>
              </div>
            </div>
          )}

          <div
            className="msg-template-options"
            onClick={(event) => event.stopPropagation()}
          >
            <$Tooltip
              placement="top"
              title={t("US.COLLECTION.COMMON:COMMON.EDIT")}
            >
              <$Button
                size="small"
                onClick={() => {
                  onPreviewEditDrawer &&
                    onPreviewEditDrawer(false, messageTemplate);
                }}
                disabled={metaData && isSubCaseTransferredToCase(metaData)}
                className="ml-1"
              >
                <EditOutlined />
              </$Button>
            </$Tooltip>
            <$Tooltip
              placement="top"
              title={t("US.COLLECTION.CASE:MASSAGETEMPLATES.EDIT_DISPLAY_NAME")}
            >
              <$Button
                size="small"
                onClick={() => editDisplayName()}
                disabled={metaData && isSubCaseTransferredToCase(metaData)}
                className="ml-1"
              >
                <BoxIcons type={IconTypes.BOX_ICON} name="rename" />
              </$Button>
            </$Tooltip>
            <$Popconfirm
              title={t("US.COLLECTION.CASE:MASSAGETEMPLATES.DELETECONFIRM")}
              onConfirm={onDelete}
              okText={t("US.COLLECTION.COMMON:COMMON.YES")}
              cancelText={t("US.COLLECTION.COMMON:COMMON.NO")}
            >
              <$Tooltip
                placement="top"
                title={t("US.COLLECTION.COMMON:COMMON.DELETE")}
              >
                <$Button
                  size="small"
                  disabled={metaData && isSubCaseTransferredToCase(metaData)}
                  className="ml-1"
                >
                  <DeleteOutlined className="text-error" />
                </$Button>
              </$Tooltip>
            </$Popconfirm>
            <$Popconfirm
              title={
                isPin
                  ? t(
                      "US.COLLECTION.CASE:MASSAGETEMPLATES.ARE_YOU_SURE_YOU_WANT_TO_UNPIN?"
                    )
                  : t(
                      "US.COLLECTION.CASE:MASSAGETEMPLATES.ARE_YOU_SURE_YOU_WANT_TO_PIN?"
                    )
              }
              onConfirm={onPinUnpinTemplate}
              okText={t("US.COLLECTION.COMMON:COMMON.YES")}
              cancelText={t("US.COLLECTION.COMMON:COMMON.NO")}
            >
              <$Tooltip
                placement="top"
                title={
                  isPin
                    ? t("US.COLLECTION.COMMON:COMMON.UNPIN")
                    : t("US.COLLECTION.COMMON:COMMON.PIN")
                }
              >
                <$Button
                  size="small"
                  disabled={metaData && isSubCaseTransferredToCase(metaData)}
                  className="ml-1"
                >
                  {isPin ? <PushpinFilled /> : <PushpinOutlined />}
                </$Button>
              </$Tooltip>
            </$Popconfirm>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessageTemplate;
